import type { InferOutput } from 'valibot'
import { array, nullish, number, object, picklist, pipe, string, transform, union } from 'valibot'
import { bodytext, lastUpdatedByEditor } from './solr-fields'

export const nodeTypeSchema = picklist([
  'law',
  'chapter',
  'section',
  'subsection',
  'note',
  'footnote',
  'legal_commentary',
  'legal_commentary_section',
  'book',
  'book_part',
  'book_page',
  'book_chapter',
  'book_metadata',
  'journal_section',
  'journal_article',
  'journal_editorial',
  'journal_colophon',
  'journal',
  'academic_article_section',
  'academic_article',
  'author',
  'news_notice',
  'book_attachment',
], ({ received }) => `Invalid node type: ${received}`)

export type NodeType = InferOutput<typeof nodeTypeSchema>

const baseDocumentSchema = object({
  nodeType: nodeTypeSchema,
  bodytext,
  author: nullish(array(string())),
  lastUpdatedByEditor,
  id: string(),
  title: nullish(string()),
  shortTitle: nullish(string()),
  timestamp: nullish(string()),
  author_list: nullish(array(string())),
  legal_areas: nullish(array(string())),
  coverImage: nullish(union([string(), array(string())])),
  path_location: nullish(array(string())),
  path_title: nullish(array(string())),
  dateOfPublication: nullish(string()),
  rootId: nullish(string()),
})

const lawDocSchema = object({
  ...baseDocumentSchema.entries,
  chapterTitle: nullish(string()),
  dateInForce: nullish(string()),
  lastChangeInForce: nullish(string()),
  paragraphTitle: nullish(string()),
  source: nullish(string()),
})

const lawCommentaryDocSchema = object({
  ...baseDocumentSchema.entries,
  lawId: nullish(string()),
  lawTitle: nullish(string()),
  lawFragmentId: nullish(string()),
  authorName: nullish(union([array(string()), string()])),
  ezMainNodeId: nullish(number()),
})

const literatureDocSchema = object({
  ...baseDocumentSchema.entries,
  year: nullish(string()),
  publisher: nullish(pipe(array(
    string(),
  ), transform(it => it?.[0] || ''))),
  editionNumber: nullish(string()),
})

const authorDocSchema = object({
  ...baseDocumentSchema.entries,
  remoteId: nullish(string()),
  image: nullish(union([string(), array(string())])),
})

const journalDocSchema = object({
  ...baseDocumentSchema.entries,
  abstract: nullish(string()),
  publish_date: nullish(pipe(array(
    string(),
  ), transform(dates => dates?.[0] || ''))),
  publisher: nullish(array(string())),
  publisherId: nullish(string()),
  authors: nullish(array(string())),
  journalId: nullish(string()),
  issue: nullish(string()),
  volume: nullish(string()),
  subTitle: nullish(string()),
  journalSectionTitle: nullish(string()),
})

export const globalSearchSchema = object({
  ...lawDocSchema.entries,
  ...literatureDocSchema.entries,
  ...lawCommentaryDocSchema.entries,
  ...authorDocSchema.entries,
  ...journalDocSchema.entries,
})

export type GlobalSearch = InferOutput<typeof globalSearchSchema>
